import apiClient from './apiClient';
import { ENDPOINTS } from './apiConfig';

/**
 * ------------------------------------------------------------------
 * SERVICIO DE CONCEPTOS DE IMPUESTOS (TAX CONCEPTS)
 * ------------------------------------------------------------------
 * Maneja las operaciones de lectura para conceptos de impuestos
 * Basado en la documentación:
 * GET    /api/tax-concepts        -> List all tax concepts
 * GET    /api/tax-concepts/{id}   -> Get details by ID
 * ------------------------------------------------------------------
 */

/**
 * Normaliza los datos del concepto desde la API
 * Asegura tipos de datos consistentes y formatea el código con ceros a la izquierda
 */
const normalizeConceptData = (apiData) => {
    if (!apiData) return null;

    // Formatear idconceptos con ceros a la izquierda (001, 002, etc.)
    const formattedId = String(apiData.idconceptos || '').padStart(3, '0');

    return {
        idconceptos: formattedId,
        // La API devuelve el tipo de persona (PNR, PJD) en el campo 'tipo'.
        // Mapeamos esto a 'subtipo' y forzamos 'tipo' a 'ISLR' para que el filtro de AppConfigContext funcione.
        tipo: 'ISLR',
        subtipo: apiData.tipo || '',
        descripcion_abreviada: apiData.descripcion_abreviada || '',
        porcentaje: apiData.porcentaje || '0.00',
        sustraendo: apiData.sustraendo || '0.00',
        // Campos adicionales útiles para el frontend
        id: formattedId,
        descripcion: apiData.descripcion_abreviada || '',
        code: formattedId,
        label: apiData.descripcion_abreviada || ''
    };
};

const taxConceptsService = {
    /**
     * Obtener todos los conceptos de impuestos
     * @returns {Promise<Array>} Lista de conceptos
     */
    getAll: async () => {
        try {
            const response = await apiClient.get(ENDPOINTS.TAX_CONCEPTS);
            const data = response;

            // Normalizar cada concepto
            return Array.isArray(data) ? data.map(normalizeConceptData) : [];
        } catch (error) {
            console.error('Error fetching tax concepts:', error);
            throw error;
        }
    },

    /**
     * Obtener un concepto por ID
     * @param {string|number} id - ID del concepto
     * @returns {Promise<Object>} Datos del concepto
     */
    getById: async (id) => {
        try {
            const response = await apiClient.get(ENDPOINTS.TAX_CONCEPT_BY_ID(id));
            const data = response.data || response;
            return normalizeConceptData(data);
        } catch (error) {
            console.error(`Error fetching tax concept ${id}:`, error);
            throw error;
        }
    },

    /**
     * Obtener conceptos filtrados por tipo
     * @param {string} tipo - Tipo de concepto (ISLR, IVA, etc.)
     * @returns {Promise<Array>} Lista de conceptos filtrados
     */
    getByTipo: async (tipo) => {
        try {
            const allConcepts = await taxConceptsService.getAll();
            return allConcepts.filter(concept =>
                concept.tipo && concept.tipo.toUpperCase() === tipo.toUpperCase()
            );
        } catch (error) {
            console.error(`Error fetching tax concepts by tipo ${tipo}:`, error);
            throw error;
        }
    },

    /**
     * Obtener conceptos formateados para usar en selects
     * @param {string} tipo - Opcional: filtrar por tipo
     * @returns {Promise<Array>} Array de objetos {value, label}
     */
    getAsOptions: async (tipo = null) => {
        try {
            let concepts = await taxConceptsService.getAll();

            if (tipo) {
                concepts = concepts.filter(c =>
                    c.tipo && c.tipo.toUpperCase() === tipo.toUpperCase()
                );
            }

            return concepts.map(concept => ({
                value: concept.idconceptos,
                label: `${concept.idconceptos} - ${concept.descripcion_abreviada}`,
                porcentaje: concept.porcentaje,
                tipo: concept.tipo
            }));
        } catch (error) {
            console.error('Error formatting tax concepts as options:', error);
            throw error;
        }
    }
};

export default taxConceptsService;
